import { EventEmitter, Injectable, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationProxyService } from '../../shared/services/applicationproxy.service';
import { TransmitService } from './transmit.service';
import { ApplicationActions } from '../../actions/application.actions';
import { ModalService } from '../../shared/services/modal.service';
import { CookieService } from 'angular2-cookie/core';
import { PromoCode } from '../models/Enum/PromoCode';
import * as _ from 'lodash';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class FlowManagerService {
  @Output() pageErrorObj = new EventEmitter();
  @Output() whyWeAskCopyChange = new EventEmitter();

  valuePropCopyChange = new BehaviorSubject();
  faqCopyChange = new BehaviorSubject();
  accountType = '';
  applyForMargin = 'false';
  applyForOption = 'false';
  investmentSpeculationGroup = 'individual_account_type';
  marriedSelected = false;
  whyWeAsk = {};
  retirementPath = {};
  logoutUrl = 'https://www.ally.com/logged-off';
  // @Todo: Move all of this indo Redux

  constructor(
    appProxy: ApplicationProxyService,
    transmitService: TransmitService,
    appActions: ApplicationActions,
    modal: ModalService,
    router: Router,
    cookies: CookieService
  ) {
    this.appProxy = appProxy;
    this.transmitService = transmitService;
    this.appActions = appActions;
    this.modal = modal;
    this.router = router;
    this.cookies = cookies;
    this.configObj = {};
    this.exps = [];
    this._isTest = false;
    this.pageErrorObjVal = { hasErrors: false, errorCount: 0 };
    this.unlistedBroker = false;
    this.validDTC = true;
    this.aCHProfile = false;
    this.manualACHProfile = false;
    this.fundedAccount = false;
    this.completedCip = false;
    this.settings = {};
    this.recurringDeposit = false;
    this.completedQx = false;
    this.allyRoutingError$ = new BehaviorSubject();
  }

  resetPageError() {
    this.pageError = { hasErrors: false, errorCount: 0 };
  }

  set pageError(obj) {
    this.pageErrorObjVal = obj;
    this.pageErrorObj.emit(this.pageErrorObjVal);
  }

  get pageError() {
    return this.pageErrorObjVal;
  }

  set updateValueProp(value) {
    this.valuePropCopyChange.next(value);
  }

  set updateFAQ(value) {
    this.faqCopyChange.next(value);
  }

  getWhyWeAsk() {
    return this.whyWeAsk;
  }

  setWhyWeAsk(value) {
    this.whyWeAsk = value;
    this.whyWeAskCopyChange.emit(value);
  }

  get goalSession() {
    return this.appActions.getData('goalSession', null);
  }

  set goalSession(value) {
    this.appActions.setData('goalSession', value);
  }

  async clearSessionAndLogout() {
    await this.transmitService.transmitigatorLogout();
    await this.appProxy.clearSession();
    window.location.href = this.logoutUrl;
  }

  setFakeGoalSession() {
    const goalSession = {
      goal: 'purchase',
      timeHorizon: 20,
      accountTaxType: 'taxAdvantaged',
      liquidAssets: '123123',
      initialInvestment: '1212',
      riskTolerance: 'low',
      strategy: 'CashEnhancedCore',
      isCashVested: true,
      portfolioTier: 3,
      isDirty: true,
    };

    this.appActions.setData('goalSession', goalSession);
  }

  get isRetirement() {
    return this.appActions.getData('goalSession.goal') === 'retirement';
  }

  set completedQuestionnaire(bool) {
    this.completedQx = bool;
  }

  get completedQuestionnaire() {
    return this.completedQx;
  }

  resetStrategy() {
    this.appActions.setData('goalSession.strategy', null);
  }

  get submitError() {
    return this.appActions.getData('submitError', true);
  }

  set submitError(value) {
    this.appActions.setData('submitError', value);
  }

  get fundingMethod() {
    return this.appActions.getData('fundingMethod', 'ACH');
  }

  set fundingMethod(value) {
    this.appActions.setData('fundingMethod', value);
  }

  get guid() {
    return this.appActions.getData('guid', '');
  }

  set guid(value) {
    this.appActions.setData('guid', value);
  }

  get applicationId() {
    return this.appActions.getData('applicationId', 0);
  }

  set applicationId(value) {
    this.appActions.setData('applicationId', value);
  }

  get requestId() {
    return this.appActions.getData('requestId', 0);
  }

  set requestId(value) {
    this.appActions.setData('requestId', value);
  }

  get accountStatus() {
    return this.appActions.getData('accountStatus', null);
  }

  set accountStatus(value) {
    this.appActions.setData('accountStatus', value);
  }

  set isSubmitting(value) {
    this.appActions.setData('isSubmitting', value);
  }

  get isSubmitting() {
    return this.appActions.getData('isSubmitting');
  }

  set isSubmittingDAPI(value) {
    this.appActions.setData('isSubmittingDAPI', value);
  }

  get isSubmittingDAPI() {
    return this.appActions.getData('isSubmittingDAPI');
  }

  get acmGuid() {
    return this.appActions.getData('acmGuid');
  }

  set acmGuid(value) {
    this.appActions.setData('acmGuid', value);
  }

  get promoCode() {
    if (this.promo !== undefined && typeof this.promo !== 'boolean') {
      if (PromoCode.indexOf(this.promo.toUpperCase()) <= -1) {
        this.promo = false;
      }
    }
    return this.promo || false;
  }

  set promoCode(value) {
    this.promo = value;
  }

  set isTrustedContactSkipped(value) {
    this.appActions.setData('isTrustedContactSkipped', value);
  }

  get isTrustedContactSkipped() {
    return this.appActions.getData('isTrustedContactSkipped');
  }

  // Need this to see what app is loaded
  getAppType() {
    return this.appActions.getData('appType');
  }

  setAppType(value) {
    this.appActions.setData('appType', value);
  }

  get isWBRedirect() {
    return this.appActions.getData('wbRedirect');
  }

  setWBRedirect(value) {
    // for alloy welcome back redirect
    this.appActions.setData('wbRedirect', value);
  }

  getAccountType() {
    return this.appActions.getData('ApplicationInformation.accountType', '');
  }

  setAccountType(value) {
    this.appActions.setData('ApplicationInformation.accountType', value);
  }

  get config() {
    return this.configObj;
  }

  set config(value) {
    this.configObj = value;
  }

  get environment() {
    return this.appActions.getData('environment', 'dev');
  }

  set environment(value) {
    this.appActions.setData('environment', value);
  }

  get isIra() {
    const iraTypes = ['select-roth', 'select-traditional', 'select-rollover'];

    return iraTypes.indexOf(this.getAccountType()) >= 0;
  }

  setAuthed(value) {
    this.appActions.setData('auth.authed', value);
  }

  get isAuthed() {
    return this.appActions.getData('auth.authed', false);
  }

  fundLaterRouting() {
    if (this.appActions.getData('auth.authed')) {
      this.ssoToAlly();
    } else {
      this.router.navigate(['/credentials']);
    }
  }

  set isTkCustomer(value) {
    this.appActions.setData('auth.tkCustomer', value);
  }

  get isTkCustomer() {
    return this.appActions.getData('auth.tkCustomer', false);
  }

  getinvestmentSpeculationGroup() {
    return this.investmentSpeculationGroup;
  }

  setinvestmentSpeculationGroup(value) {
    this.investmentSpeculationGroup = value;
  }

  setValidationSchema(value) {
    this.validationSchema = value;
  }

  setIsCashVested(value) {
    this.appActions.setData('goalSession.isCashVested', value);
  }

  getValidationSchema() {
    return this.validationSchema || {};
  }

  getApplyForMargin() {
    return this.appActions.getData('GoalInformation.applyForMargin', false);
  }

  getApplyForOption() {
    return this.appActions.getData('GoalInformation.applyForOption', false);
  }

  getEnrollInFPSL() {
    return this.appActions.getData('OptInInformation.OptInFpsl', false);
  }

  get isMarried() {
    return (
      this.appActions.getData('PersonalInformation.maritalStatus', 'none') ===
      'Married'
    );
  }

  set hasExperiments(value) {
    this.hasExp = value;
  }

  get hasExperiments() {
    return this.hasExp;
  }

  setSettings(value) {
    this.settings = value;
  }

  getSettings() {
    return this.settings;
  }

  set experiments(experiments) {
    _.each(experiments, (value, key) => {
      this.cookies.put(key, value, {
        expires: moment().add(365, 'days').toDate(),
      });
    });
  }

  get experiments() {
    const allCookies = this.cookies.getAll();
    const experiments = _.pickBy(allCookies, (value, key) => {
      return key.match(/^experiment/);
    });

    return experiments;
  }

  removeExperiment(value) {
    this.cookies.remove(value);
  }

  set dtmExperiments(experiments) {
    _.each(experiments, (experimentVariation, experimentId) => {
      if (
        _.find(this.exps, (exp) => exp.experimentId === experimentId) ===
        undefined
      ) {
        this.exps.push({ experimentId, experimentVariation });
      }
    });
  }

  get dtmExperiments() {
    return this.exps;
  }

  get canUnload() {
    return this.appActions.getData('canUnload', false);
  }

  set canUnload(value) {
    this.appActions.setData('canUnload', true);
  }

  toPercent(value) {
    return `${value}%`;
  }

  refreshApp() {
    const pathname = document.location.pathname.split('/')[1];
    let location = `${document.location.origin}/${pathname}`;

    // @TODO Fire off DTM With Field Level Errors
    this.appActions.setData('sendDTM', true);
    if (this.getAppType() === 'tka') {
      location += '?app=tka';
    }
    this.canUnload = true;
    window.location = location;
  }

  async ssoToAlly(link = 'dashboard') {
    this.modal.loadingModel();
    try {
      const isTransmitFlow = this.transmitService.getIncomingTicketID();
      const param = {
        destination: link,
        ticketID: isTransmitFlow,
      };
      const dashUrl = await this.appProxy.getDashboardUrl(param);
      if (isTransmitFlow) {
        const response = await this.transmitService.generateTicketId();
        if (dashUrl && response) {
          this.canUnload = true;
          const url = new URL(dashUrl.url);
          url.searchParams.append('ticketID', response.ticketID);
          await this.appProxy.clearSession();
          window.location.href = url;
        } else {
          throw new Error('Unable to generate ticketID');
        }
      } else {
        this.canUnload = true;
        window.location.href = dashUrl.url;
      }
    } catch (error) {
      console.log(error);
    }
  }

  getHandshakeFinished() {
    return this.appActions.getData('handshake', false);
  }

  setHandshakeFinished(value) {
    this.appActions.setData('handshake', value);
  }

  set prepoppedTKData(value) {
    this.appActions.setData('prepoppedTKData', value);
  }

  get prepoppedTKData() {
    return this.appActions.getData('prepoppedTKData', false);
  }

  set prepoppedValid(value) {
    this.appActions.setData('prepoppedValid', value);
  }

  get prepoppedValid() {
    return this.appActions.getData('prepoppedValid', false);
  }

  getStepArrays(currentStep) {
    // 'Employment Information',
    //       'Financial Information',
    //       'Investment Objective',
    //       'Joint Holder Personal Information',
    //       'Joint Holder Financial Information'
    // 'Custodial Account Information (for Minor)'
    // 'Beneficiary Information'

    // Little change for beneficiary (as is tradition)
    const step = currentStep.replace(
      'edit-beneficiary-info',
      'primary-beneficiary-info'
    );

    const appType = this.getAppType();
    const accountType = this.getAccountType();
    const allSteps = [];

    if (this.isAuthed) {
      allSteps.push({
        route: 'personal-info-auth',
        stepName: 'Personal Information',
      });
    } else {
      allSteps.push({
        route: 'personal-info',
        stepName: 'Personal Information',
      });
    }
    allSteps.push({
      route: 'employment-info',
      stepName: 'Employment Information',
    });
    allSteps.push({
      route: 'financial-info',
      stepName: 'Financial Information',
    });

    allSteps.push({
      route: 'trusted-contact',
      stepName: 'Trusted Contact',
    });

    if (appType === 'tks') {
      allSteps.push({
        route: 'investment-objective',
        stepName: 'Investment Objective',
      });

      if (accountType !== 'select-custodial') {
        allSteps.push({
          route: 'investment-preferences',
          stepName: 'Investment Preferences',
        });
      }
    }

    if (
      accountType === 'select-joint' ||
      currentStep === 'joint-financial-info' ||
      currentStep === 'joint-info'
    ) {
      allSteps.push({
        route: 'joint-info',
        stepName: 'Joint Holder Personal Information',
      });
      allSteps.push({
        route: 'joint-financial-info',
        stepName: 'Joint Holder Financial Information',
      });
    }

    if (
      accountType === 'select-custodial' ||
      currentStep === 'custodial-minor-info'
    ) {
      allSteps.push({
        route: 'custodial-minor-info',
        stepName: 'Custodial Account Information (for Minor)',
      });
    }

    if (
      this.isIra === true ||
      currentStep === 'edit-beneficiary-info' ||
      currentStep === 'primary-beneficiary-info'
    ) {
      allSteps.push({
        route: 'primary-beneficiary-info',
        stepName: 'Beneficiary Information',
      });
    }

    const stepIndex = _.findIndex(allSteps, (s) => s.route === step);
    const stepsComplete = _.slice(allSteps, 0, stepIndex);
    const stepsToComplete = _.slice(allSteps, stepIndex + 1);

    return {
      stepsComplete,
      stepsToComplete,
    };
  }

  logContactInfo() {
    const payload = {
      guid: this.guid,
      email: this.appActions.getData('YourInformation.email'),
      firstName: this.appActions.getData('YourInformation.firstName'),
      lastName: this.appActions.getData('YourInformation.lastName'),
      middleInitial:
        this.appActions.getData('YourInformation.middleInitial') || '',
      appType: this.getAppType(),
      accountType: this.getAccountType(),
    };

    this.appProxy.logContactInfo(payload, (resp) => {
      // console.log(resp);
    });
  }

  set isUnlistedBroker(bool) {
    this.unlistedBroker = bool;
  }

  get isUnlistedBroker() {
    return this.unlistedBroker;
  }

  set isValidDTC(bool) {
    this.validDTC = bool;
  }

  get isValidDTC() {
    return this.validDTC;
  }

  resetAcats() {
    this.isUnlistedBroker = false;
    this.appActions.deleteData('Acats');
  }

  resetAcatsFirm() {
    this.isUnlistedBroker = false;
    this.appActions.deleteData('Acats.acatsFirmName');
    this.appActions.deleteData('Acats.acatsFirmDTC');
    this.appActions.deleteData('Acats.acatsFirmAccount');
  }

  // Condiitonal back to funding button on Credentials
  set hasACHProfile(bool) {
    this.aCHProfile = bool;
  }

  get hasACHProfile() {
    return this.aCHProfile;
  }

  set hasManualACHProfile(bool) {
    this.manualACHProfile = bool;
  }

  get hasManualACHProfile() {
    return this.manualACHProfile;
  }

  set hasFundedAccount(bool) {
    this.fundedAccount = bool;
  }

  get hasFundedAccount() {
    return this.fundedAccount;
  }

  get accountTaxType() {
    return this.tkaAccountTaxType;
  }

  set accountTaxType(value) {
    this.tkaAccountTaxType = value;
  }

  get completedCipForm() {
    return this.completedCip;
  }

  set completedCipForm(bool) {
    this.completedCip = bool;
  }

  set isOneAllyBank(bool) {
    this.oneAllyBank = bool;
  }

  get isOneAllyBank() {
    return this.oneAllyBank;
  }

  set hasDepositAccounts(bool) {
    this.depositAccounts = bool;
  }

  get hasDepositAccounts() {
    return this.depositAccounts;
  }

  set isRecurringDeposit(bool) {
    this.recurringDeposit = bool;
  }

  get isRecurringDeposit() {
    return this.recurringDeposit;
  }

  set isAllyRoutingError(bool) {
    this.allyRoutingError$.next(bool);
  }

  phoneNumberMasking(value) {
    let maskedInput = value.split('');
    if (maskedInput.length > 2) {
      maskedInput.splice(3, 0, '-');
      if (maskedInput.length > 6) {
        maskedInput.splice(7, 0, '-');
      }
    }
    maskedInput = maskedInput.join('');
    return maskedInput;
  }

  setEsignOnFile(bool) {
    this.appActions.setData('ConsentList.eSign', bool);
  }

  getEsignOnFile() {
    return this.appActions.getData('ConsentList.eSign', false);
  }

  updatePersonalInfoACMRedux(cb) {
    // const guid = '382747856948737202'; // guid
    // make a call to ACM and get the user personal Info by fectching guid from the session on the node
    this.appProxy.getCustByGuid((resp) => {
      if (resp.status === 400) {
        this.modal.ssnModel();
        return;
      }
      try {
        // update the redux store with the response
        if (resp.result.AcmResponse.NoOfRecords > 0) {
          const responseData =
            resp.result.AcmResponse.Payload.CustomerSearchResponse
              .SearchResult[0];
          const addressData = responseData.PrimaryResidenceList[0].Address;
          const hasSSN = !!responseData.SocialSecurityNumber;

          this.appActions.setData(
            'PersonalInformation.address.street',
            addressData.lineOne
          );
          this.appActions.setData(
            'PersonalInformation.address.street2',
            addressData.lineTwo
          );
          this.appActions.setData(
            'PersonalInformation.address.city',
            addressData.city
          );
          this.appActions.setData(
            'PersonalInformation.address.state',
            addressData.provincestate
          );
          this.appActions.setData(
            'PersonalInformation.address.zip',
            addressData.zipPostalCode
          );

          this.appActions.setData(
            'PersonalInformation.citizenship',
            responseData.citizenship
          );

          const ssn = hasSSN
            ? responseData.SocialSecurityNumber.GovernmentIssuedIdentifier
                .identificationNumber
            : responseData.TaxIdentificationNumber.GovernmentIssuedIdentifier
                .identificationNumber;
          this.appActions.setData('PersonalInformation.ssn', ssn);

          const formatted = moment
            .utc(responseData.DateOfBirth)
            .format('MM/DD/YYYY');
          this.appActions.setData('PersonalInformation.dob', formatted);

          const phNo =
            responseData.HomeTelephoneList && responseData.HomeTelephoneList[0]
              ? responseData.HomeTelephoneList[0].Contact.contactValue
              : responseData.AlternateTelephoneList[0].Contact.contactValue;

          const formattedPhno = this.phoneNumberMasking(
            phNo.replace(/[^a-z\d\s]+/gi, '')
          );
          this.appActions.setData(
            'PersonalInformation.mobilePhone',
            formattedPhno
          );

          this.appActions.setData(
            'YourInformation.firstName',
            responseData.Legal.Name.firstName
          );
          // Have the below logic in 2 lines to fix teamcity build issue
          this.appActions.setData(
            'YourInformation.middleInitial',
            responseData.Legal.Name.middleInitial ||
              responseData.Legal.Name.middleName
          );
          this.appActions.setData(
            'YourInformation.lastName',
            responseData.Legal.Name.lastName
          );
          this.appActions.setData(
            'YourInformation.email',
            responseData.PersonalEmailList[0].Contact.contactValue
          );
          this.appActions.setData(
            'YourInformation.confirmEmail',
            responseData.PersonalEmailList[0].Contact.contactValue
          );

          // prettier-ignore
          // eslint-disable-next-line max-len
          this.appActions.setData('YourInformation.fullName', `${responseData.Legal.Name.firstName} ${responseData.Legal.Name.middleInitial || ''} ${responseData.Legal.Name.lastName}`);

          this.logContactInfo();

          if (cb) {
            cb();
          }
        }
      } catch (err) {
        throw new Error(`Error - Flowmanager service: ${err}`);
      }
    });
  }
}
